import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import React, { useEffect, useRef, useState } from 'react';
import { Card, Dropdown, DropdownButton } from 'react-bootstrap';
import { Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { PurchaseOrderHandle } from '../PurchaseOrder/CreatePurchaseOrderForm';
import UpdateIndividualForm from './UpdateIndividualForm';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';

interface CustomerDetails {
  customerId: number;
  firstName: string;
  lastName: string;
  membershipNumber: string;
  email: string;
  contactNumber: string;
  addressLine1: string;
  addressLine2: string;
  barangay: string;
  city: string;
  province: string;
  zipCode: string;
  isAMechanic: boolean;
  individualCustomerMechanicType: 'SelfEmployed' | 'CompanyEmployed';
  profession: string;
  workCompanyName: string;
}

const IndividualCustomerDetails = () => {
  const { id: customerId } = useParams();
  const [customerDetails, setCustomerDetails] =
    useState<CustomerDetails | null>(null);
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const updateIndividualCustomerFormRef = useRef<PurchaseOrderHandle>(null);

  const fetchCustomerDetails = async () => {
    try {
      const res = await axiosInstance(
        `/api/v1/customers/individuals/${customerId}`
      );
      setCustomerDetails(res.data);
      console.log(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const updateIndividualCustomer = () => {
    setModalProps({
      title: 'Create Customer',
      content: (
        <UpdateIndividualForm
          ref={updateIndividualCustomerFormRef}
          customerId={customerId}
          firstName={customerDetails?.firstName as string}
          lastName={customerDetails?.lastName as string}
          email={customerDetails?.email as string}
          contactNumber={customerDetails?.contactNumber as string}
          addressLine1={customerDetails?.addressLine1 as string}
          addressLine2={customerDetails?.addressLine2 as string}
          barangay={customerDetails?.barangay as string}
          city={customerDetails?.city as string}
          province={customerDetails?.province as string}
          zipCode={customerDetails?.zipCode as string}
          isAMechanic={customerDetails?.isAMechanic ?? false}
          mechanicType={
            customerDetails?.individualCustomerMechanicType as
              | 'SelfEmployed'
              | 'CompanyEmployed'
          }
          profession={customerDetails?.profession as string}
          workCompanyName={customerDetails?.workCompanyName as string}
          membershipCardNumber={customerDetails?.membershipNumber as string}
        />
      ),
      onSubmit: () => handleSubmitIndividualCustomer(),
      onCancel: () => modalRef.current.hide()
    });
    modalRef.current.show();
  };

  const handleSubmitIndividualCustomer = async () => {
    if (updateIndividualCustomerFormRef.current) {
      try {
        const isSuccess =
          await updateIndividualCustomerFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          fetchCustomerDetails();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleResendWelcome = async () => {
    try {
      await axiosInstance.post(
        `/api/v1/customers/individuals/${customerId}/resendWelcome`
      );
      toast.success('Welcome notification resent successfully', {
        ...toastConfig,
        autoClose: 2000
      });
    } catch (error) {
      console.error('Error resending welcome notification:', error);
      toast.error('Failed to resend welcome notification', {
        ...toastConfig,
        autoClose: 2000
      });
    }
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  return (
    <>
      <Row lg="auto" className="d-flex justify-content-between mb-3">
        <h4>Customer Details</h4>
        <DropdownButton title="Actions" variant="phoenix-primary">
          <Dropdown.Item onClick={updateIndividualCustomer}>
            Update Details
          </Dropdown.Item>
          <Dropdown.Item onClick={handleResendWelcome}>
            Resend Welcome
          </Dropdown.Item>
        </DropdownButton>
      </Row>
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between">
          <div className="w-50">
            <Card.Title as="h6">
              Customer ID: {customerDetails?.customerId}
            </Card.Title>
            <Card.Title as="h6">
              First Name: {customerDetails?.firstName}
            </Card.Title>
            <Card.Title as="h6">
              Last Name: {customerDetails?.lastName}
            </Card.Title>
            <Card.Title as="h6">
              Membership Number: {customerDetails?.membershipNumber}
            </Card.Title>
            <Card.Title as="h6">Email: {customerDetails?.email}</Card.Title>
            <Card.Title as="h6">
              Contact Number: {customerDetails?.contactNumber}
            </Card.Title>
          </div>
          <div className="w-50">
            <Card.Title as="h6">
              Address: {customerDetails?.addressLine1}
            </Card.Title>
            <Card.Title as="h6">
              Zip Code: {customerDetails?.zipCode}
            </Card.Title>
            <Card.Title as="h6">
              Profession: {customerDetails?.profession}
            </Card.Title>
            <Card.Title as="h6">
              Company Name: {customerDetails?.workCompanyName}
            </Card.Title>
            {customerDetails?.isAMechanic === true && (
              <Card.Title as="h6">
                Mechanic Type: {customerDetails?.individualCustomerMechanicType}
              </Card.Title>
            )}
          </div>
        </Card.Body>
      </Card>
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default IndividualCustomerDetails;
